import * as Sentry from '@sentry/react';
import {
  Component,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import {
  isChunkLoadError,
  resetChunkReloadAttempt,
} from '@utils/lazyImportWithRetry';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Catches lazy-chunk load failures from the route tree and shows a recoverable
// UI. Non-chunk errors are re-thrown so the top-level Sentry ErrorBoundary in
// main.tsx handles them. Without this, a hung or blocked route chunk renders to
// a blank screen with no signal (Suspense pending forever, no error event).
class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: info.componentStack } },
        tags: { error_kind: 'chunk_load' },
      });
    }
  }

  handleRetry = () => {
    resetChunkReloadAttempt();
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (error) {
      if (!isChunkLoadError(error)) {
        throw error;
      }
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center max-w-md px-6">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">
              We can't load this page
            </h1>
            <p className="text-gray-500 mb-6">
              Part of the app couldn't load. This is usually a network issue —
              an ad blocker, VPN, corporate proxy, or antivirus. Try again, or
              switch to a different browser or network.
            </p>
            <button
              onClick={this.handleRetry}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default RouteErrorBoundary;
